import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ListViewRow } from '../../../core/model/list-view-row';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Invoice } from '../../../invoice/model/invoice';
import { LeaseAgreementService } from '../../service/data/lease-agreement.service';

@Component({
    selector: 'app-void-lease-agreement',
    templateUrl: './void-lease-agreement.component.html',
    styleUrls: ['./void-lease-agreement.component.scss'],
    providers: [NavigationService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VoidLeaseAgreementComponent implements OnDestroy, OnInit {
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _fcf: FormControlExFactory,
    private _navigation: NavigationService,
    private _leaseAgreementService: LeaseAgreementService,
    private _fb: FormBuilder,
    private _messageService: MessageService,
    private _cf: ColumnDefinitionFactory
  ) {
    this._subscriptions = new Subscription();

    this.form = this._fb.group({
      invoices: this._fb.control([]),
      invoiceIds: this._fb.control([]),
      voidDescription: this._fcf.create('COMMON.NOTE', ''),
    });

    this.invoicesColumnDefinitions = [
      this._cf.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      this._cf.create('INVOICE.RECIPIENT_NAME', 'invoiceRecipient'),
      this._cf.create('INVOICE.PERIOD', 'refersToPeriod'),
      this._cf.createDate('DUE_PERIODS.INVOICE_PERIOD_FROM', 'periodFrom', ColumnVisibility.optional),
      this._cf.createDate('DUE_PERIODS.INVOICE_PERIOD_TO', 'periodTo', ColumnVisibility.optional),
      this._cf.createDate('INVOICE.DUE_DATE', 'dueDate'),
      this._cf.createMoney('INVOICE.REMAINING_AMOUNT', 'remainingAmount'),
      this._cf.createMoney('INVOICE.INVOICEAMOUNT', 'totalAmount'),
    ];
  }

  invoicesColumnDefinitions: ColumnDefinition[];
  invoices: ListViewRow[];
  form: FormGroup;

  leaseAgreementId: string;
  leaseAgreementNumber: string;

  get displayCreditInvoices(): boolean {
    return this.invoices !== undefined && this.invoices.length > 0;
  }

  ngOnInit() {
    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('leaseAgreementId');
            if (id) {
              this.leaseAgreementId = id;
              this.AddSubscription(
                this._leaseAgreementService.getDataForVoidOfLeaseAgreement(id).subscribe(data => {
                  this._setLeaseAgreementData(data);
                })
              );
              return of([]);
            }
            return of([]);
          })
        )
        .subscribe(res => {})
    );
  }

  private _setLeaseAgreementData(context: any) {
    if (context) {
      this.leaseAgreementNumber = context.leaseAgreementNumber;
      var invoi = context.invoices.map(item => Object.assign(new Invoice(), item));
      this.invoices = invoi.map(inv => new ListViewRow(inv));
      this.form.controls['invoices'].setValue(this.invoices);
      this.form.controls['voidDescription'].setValue(context.voidDescription);
    }
  }

  private readonly _subscriptions: Subscription;
  private _resultSub: Subscription;

  protected AddSubscription(subscription: Subscription): void {
    if (!subscription) {
      throw new Error('Null reference. Object not set.');
    }

    this._subscriptions.add(subscription);
  }

  private clearSubscriptions() {
    if (this._resultSub && !this._resultSub.closed) this._resultSub.unsubscribe();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this.clearSubscriptions();
  }

  cancel() {
    this._navigation.navigateBack();
  }

  saving: boolean;
  save() {
    if (this.saving) return;
    this.saving = true;
    this.form.controls['invoiceIds'].setValue(this.invoices.filter(x => x.selected).map(x => x.dataSource.id));
    let context = this.form.value;
    context.id = this.leaseAgreementId;
    this.AddSubscription(
      this._leaseAgreementService
        .void(context)
        .pipe(
          finalize(() => {
            this.saving = false;
          })
        )
        .subscribe(() => {
          this._navigation.navigateBack();
        })
    );
  }
}
